import { API_BASE } from "./env";

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
  }
}

let onUnauthorized: (() => void) | null = null;

/** The app-wide 401 policy. Registered by the auth provider. */
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

export interface ApiOptions extends RequestInit {
  /**
   * Opt OUT of the global 401 redirect.
   *
   * ⚠️ Load-bearing for anything a live room depends on. A background fetch that
   * 401s mid-talk must not bounce the presenter to /login with a slide on the
   * projector; it should degrade to "unavailable" and let them keep presenting.
   */
  raw?: boolean;
}

/** One request under the app's rules: cookies sent, 401 policy applied, a non-2xx thrown as ApiError. */
async function request(path: string, opts: ApiOptions = {}): Promise<Response> {
  const { raw, ...init } = opts;
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, credentials: "include", headers });

  if (res.status === 401 && !raw) onUnauthorized?.();

  if (!res.ok) {
    let detail: unknown;
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      detail = body?.detail ?? body;
      if (typeof detail === "string") message = detail;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, message, detail);
  }
  return res;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const res = await request(path, opts);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const get = <T>(p: string, o?: ApiOptions) => api<T>(p, { ...o, method: "GET" });
export const post = <T>(p: string, body?: unknown, o?: ApiOptions) =>
  api<T>(p, { ...o, method: "POST", ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
export const patch = <T>(p: string, body?: unknown, o?: ApiOptions) =>
  api<T>(p, { ...o, method: "PATCH", body: JSON.stringify(body) });
export const del = <T>(p: string, o?: ApiOptions) => api<T>(p, { ...o, method: "DELETE" });

/**
 * Fetch a file the server names, and hand it to the browser to save.
 *
 * A fetch rather than a plain link: the request must carry the session cookie
 * to an API on another origin in development, and a failure (403, nothing
 * published) has to come back as an ApiError the page can show instead of
 * navigating the owner to a JSON error body.
 */
export async function download(path: string, fallbackName: string): Promise<string> {
  const res = await request(path, { method: "GET" });
  const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return name;
}
